import {
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  LOCALE_ID,
  Inject,
} from '@angular/core';
import { NotificationService } from '../../services/api/notification.service';
import {
  NotificationActionsService,
  MappedNotification,
  RecoveryDialogState,
} from '../../services/notifications/notification-actions.service';
import {
  normalizeNotificationType,
  extractNotificationRoute,
  extractConversationId,
  getNotificationSource,
  getNotificationActionKey,
  isProposalNotification,
  syncNotificationReadState,
  withNormalizedNotificationReadState,
} from '../../services/notifications/notification.utils';
import { Subscription, firstValueFrom } from 'rxjs';
import { CommonModule, DatePipe } from '@angular/common';
import { NotificationDetailDialogComponent } from '../notification-detail-dialog/notification-detail-dialog.component';
import { IonicModule, LoadingController, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { TranslateConfigService } from 'src/app/services/translate-config.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContactFinderDialogComponent } from '../chat/contact-finder-dialog/contact-finder-dialog.component';

@Component({
  selector: 'app-web-notifications',
  templateUrl: './web-notifications.component.html',
  styleUrls: ['./web-notifications.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, NotificationDetailDialogComponent, ContactFinderDialogComponent],
  providers: [DatePipe],
})
export class WebNotificationsComponent implements OnInit, OnDestroy {
  constructor(
    private notificationService: NotificationService,
    private notificationActions: NotificationActionsService,
    @Inject(LOCALE_ID) private locale: string,
    private datePipe: DatePipe,
    private router: Router,
    private loadingController: LoadingController,
    private toastController: ToastController,
    private translateService: TranslateConfigService,
    private translate: TranslateService
  ) {}

  show: boolean = false;
  @Output() closeBoxEvent = new EventEmitter();
  @Output() notificationSelected = new EventEmitter();

  notifications: any[] = [];
  currentPage = 1;
  totalPages = 1;
  private subscriptions: Subscription[] = [];
  loading = false;
  loadError = false;

  selectedNotification: any | null = null;
  showNotificationDialog = false;
  isContactType5 = false;
  canOpenSelectedConversation = false;
  contactPayload: { article_id?: number; interested_user_id?: number } = {};
  selectedRoute: string | null = null;
  selectedRouteArticle: any | null = null;
  sendingProposalResponse: boolean = false;
  showRecoveryOptionsDialog = false;
  recoveryDialogConversationId = 0;
  recoveryDialogPremiumChatAmount = 0;
  recoveryDialogOwnerFreeMessage = '';
  recoveryDialogOwnerFreeMsgConsumed = false;
  recoveryDialogFinderFreeMessage = '';

  toggleShow(notification: any) {
    // Marcar como leída la notificación al seleccionarla si no ha sido vista
    if (notification && !notification.viewed) {
      this.markAsRead(notification.id);
    }
    this.notificationSelected.emit(notification);
  }

  closeBox() {
    this.closeBoxEvent.emit();
  }

  loadNotifications(page: number = 1) {
    this.loading = true;
    this.loadError = false;
    const subscription = this.notificationService
      .getNotifications(page)
      .subscribe({
        next: (response) => {
          this.notifications = (response.notifications || []).map(
            (notification: any) => {
              const date = new Date(notification.created_at);
              const today = new Date();
              const formattedDate: string = date.toDateString() === today.toDateString()
                ? this.translate.instant('COMMON.TODAY')
                : this.datePipe.transform(date, 'dd MMM yy') || '';
              const timeLabel: string = this.datePipe.transform(date, 'HH:mm') || '';

              const mapped: MappedNotification = this.notificationActions.mapNotification(notification, formattedDate, timeLabel);

              return withNormalizedNotificationReadState({
                ...notification,
                ...mapped,
                // Keep web-specific field alias for template compatibility
                safeHtml: mapped.safeBody,
              });
            }
          );

          this.currentPage = response.pagination.currentPage;
          this.totalPages = response.pagination.totalPages;
          this.loading = false;
          this.loadError = false;
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
        },
      });

    this.subscriptions.push(subscription);
  }

  markAsRead(id: number) {
    const notification = this.notifications.find((n) => n.id === id);
    if (notification && !notification.viewed) {
      this.notificationService.markAsRead(id).subscribe({
        next: () => {
          syncNotificationReadState(notification);
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.translate.instant('NOTIFICATIONS.MARK_AS_READ_ERROR'),
            duration: 3000,
            color: 'danger',
          });
          await toast.present();
        },
      });
    }
  }

  deleteNotification(index: number) {
    const notification = this.notifications[index];

    this.notificationService
      .deleteNotification(notification.id, notification.viewed)
      .subscribe({
        next: () => {
          this.notifications.splice(index, 1);
          if (this.notifications.length < 1) {
            this.closeBox();
          }
        },
        error: () => {},
      });
  }

  async openNotification(notification: any) {
    if (notification && !notification.viewed) {
      this.markAsRead(notification.id);
    }

    if (isProposalNotification(notification?.originalData ?? notification)) {
      notification = await firstValueFrom(
        this.notificationActions.hydrateProposalResponse(notification)
      );
    }

    const source = getNotificationSource(notification);
    const normalizedType: number | null = normalizeNotificationType(source);
    const route: string | null = extractNotificationRoute(source);

    if (normalizedType === 4 || normalizedType === 6) {
      await this.preloadConversationAccess(source);
    }

    if (route && route.startsWith('/details/')) {
      const loaded: boolean = await this.preloadArticleRoute(route);
      if (!loaded) {
        return;
      }
    }

    this.setSelectedNotification(notification, this.selectedRouteArticle);
  }

  getSelectedNotificationActionKey(): string {
    return getNotificationActionKey(
      this.selectedNotification?.originalData ?? this.selectedNotification
    );
  }

  setSelectedNotification(notification: any, preloadedArticle: any | null = null) {
    this.selectedNotification = notification;
    this.selectedNotification.title = this.selectedNotification.originalData?.title;
    this.showNotificationDialog = true;

    const source = getNotificationSource(notification);
    const type: number | null = normalizeNotificationType(source);

    this.isContactType5 = type === 5;
    this.contactPayload = source?.data || {};
    this.selectedRoute = type === 6 ? null : extractNotificationRoute(source);
    this.selectedRouteArticle = preloadedArticle;
  }

  isSelectedProposal(): boolean {
    return isProposalNotification(this.selectedNotification);
  }

  closeNotificationDialog() {
    this.showNotificationDialog = false;
    this.selectedNotification = null;
    this.canOpenSelectedConversation = false;
    this.selectedRoute = null;
    this.selectedRouteArticle = null;
    this.sendingProposalResponse = false;
  }

  closeDialog() {
    this.closeNotificationDialog();
  }

  handleProposalSent(message: string): void {
    if (this.sendingProposalResponse) return;
    const conversationId: number | null = extractConversationId(
      this.selectedNotification?.originalData ?? this.selectedNotification
    );
    if (!conversationId) return;

    this.sendingProposalResponse = true;
    const defaultMsg: string = this.translate.instant('NOTIFICATIONS.DEFAULT_ACCEPT_MESSAGE');
    const finalMessage: string = message?.trim() || defaultMsg;

    this.notificationActions.sendProposalResponse(conversationId, message, defaultMsg).subscribe({
      next: () => {
        this.sendingProposalResponse = false;
        if (this.selectedNotification) {
          this.selectedNotification.proposalResponseText = finalMessage;
          this.selectedNotification.originalData = {
            ...(this.selectedNotification.originalData || {}),
            proposalResponseText: finalMessage,
          };
        }
        this.toastController.create({
          message: this.translate.instant('NOTIFICATIONS.PROPOSAL_RESPONSE_SENT'),
          duration: 2000,
          color: 'success',
        }).then((t) => t.present());

        this.closeNotificationDialog();
      },
      error: (err: any) => {
        this.sendingProposalResponse = false;
        const serverMsg: string = err?.error?.message ?? '';
        this.toastController.create({
          message: serverMsg || this.translate.instant('NOTIFICATIONS.PROPOSAL_RESPONSE_ERROR'),
          duration: 3000,
          color: 'danger',
        }).then((t) => t.present());
      },
    });
  }

  async accessSelectedNotification(event?: Event) {
    if (event) {
      try { event.preventDefault(); } catch (e) {}
      try { event.stopPropagation(); } catch (e) {}
      try { (event as any).stopImmediatePropagation && (event as any).stopImmediatePropagation(); } catch (e) {}
    }

    if (!this.selectedNotification) {
      return;
    }

    const selectedType = normalizeNotificationType(this.selectedNotification?.originalData ?? this.selectedNotification);
    const route: string | null = selectedType === 6
      ? null
      : (this.selectedRoute || extractNotificationRoute(this.selectedNotification));
    if (route) {
      this.closeNotificationDialog();

      if (route.startsWith('/details/') && this.selectedRouteArticle) {
        await this.router.navigate([route], {
          state: { article: this.selectedRouteArticle },
        });
        return;
      }

      await this.router.navigate([route]);
      return;
    }

    if ((this.selectedNotification.type === 4 || this.selectedNotification.type === 6) && this.canOpenSelectedConversation) {
      await this.navigateToChat(undefined, this.selectedNotification);
    }
  }

  private async preloadConversationAccess(notification: any): Promise<boolean> {
    this.canOpenSelectedConversation = false;

    const loading = await this.loadingController.create({ spinner: 'circular' });
    await loading.present();

    try {
      const accessible: boolean = await firstValueFrom(
        this.notificationActions.resolveConversationAccess(notification)
      );
      this.canOpenSelectedConversation = accessible;
      if (!accessible) {
        const toast = await this.toastController.create({
          message: this.translate.instant('NOTIFICATIONS.LOAD_ERROR'),
          duration: 2500,
          color: 'danger',
        });
        await toast.present();
      }
      return accessible;
    } catch {
      this.canOpenSelectedConversation = false;
      return false;
    } finally {
      await loading.dismiss();
    }
  }

  private async preloadArticleRoute(route: string): Promise<boolean> {
    const loading = await this.loadingController.create({ spinner: 'circular' });
    await loading.present();

    try {
      const article = await firstValueFrom(this.notificationActions.preloadArticle(route));
      if (article) {
        this.selectedRouteArticle = article;
        return true;
      }
      return false;
    } catch {
      const toast = await this.toastController.create({
        message: this.translate.instant('NOTIFICATIONS.LOAD_ERROR'),
        duration: 2500,
        color: 'danger',
      });
      await toast.present();
      return false;
    } finally {
      await loading.dismiss();
    }
  }

  get selectedTitle(): string {
    if (!this.selectedNotification) return this.translate.instant('NOTIFICATIONS.DEFAULT_TITLE');
    return (
      this.selectedNotification.title ||
      this.selectedNotification.text ||
      this.translate.instant('NOTIFICATIONS.DEFAULT_TITLE')
    );
  }

  truncateText(text: string | any): string {
    if (!text) return '';

    // Si es HTML sanitizado (SafeHtml), convertir a string
    let textStr = typeof text === 'string' ? text : text.toString();

    const maxLength = 80; // Incrementado para dar más espacio al HTML
    if (textStr.length > maxLength) {
      // Para HTML, truncar sin cortar etiquetas
      let truncated = textStr.substring(0, maxLength);
      // Si cortamos en medio de una etiqueta, retroceder hasta el último >
      const lastTag = truncated.lastIndexOf('>');
      const lastOpen = truncated.lastIndexOf('<');
      if (lastOpen > lastTag) {
        // Hay una etiqueta abierta, truncar antes de ella
        truncated = truncated.substring(0, lastOpen);
      }
      return truncated + '...';
    }
    return textStr;
  }

  ngOnInit() {
    this.loadNotifications();

    // Actualizar notificaciones cuando cambie el contador
    const countSubscription =
      this.notificationService.notificationCount$.subscribe(() => {
        this.loadNotifications(this.currentPage);
      });

    this.subscriptions.push(countSubscription);
  }

  ngOnDestroy() {
    // Limpiar todas las suscripciones para evitar memory leaks
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Navega al chat según el ID proporcionado en la notificación
   */
  async navigateToChat(event?: Event, chatId?: any): Promise<void> {
    if (event) {
      try { event.preventDefault(); } catch (e) {}
      try { event.stopPropagation(); } catch (e) {}
      try { (event as any).stopImmediatePropagation && (event as any).stopImmediatePropagation(); } catch (e) {}
    }

    const notification = chatId || this.selectedNotification;
    const recoveryDialogOpened = await this.openRecoveryDialogIfNeeded(notification);
    if (recoveryDialogOpened) {
      return;
    }

    const conversationId = extractConversationId(notification?.originalData ?? notification);
    if (!conversationId) {
      return;
    }

    this.closeDialog();
    this.router.navigate(['/app/messages/' + conversationId]);
  }

  async startContactFromNotification(event?: Event) {
    if (event) {
      try { event.preventDefault(); } catch (e) {}
      try { event.stopPropagation(); } catch (e) {}
      try { (event as any).stopImmediatePropagation && (event as any).stopImmediatePropagation(); } catch (e) {}
    }
    if (!this.isContactType5) return;
    const { article_id, interested_user_id } = this.contactPayload;
    if (!article_id || !interested_user_id) {
      const t = await this.toastController.create({
        message: this.translate.instant('NOTIFICATIONS.INCOMPLETE_DATA'),
        duration: 2500,
        color: 'warning',
      });
      t.present();
      return;
    }

    const loading = await this.loadingController.create({ spinner: 'circular' });
    await loading.present();

    this.notificationActions.startContact(article_id, interested_user_id).subscribe({
      next: async (result) => {
        loading.dismiss();
        if (result.success && result.conversationId) {
          if (result.ownerNotified) {
            this.toastController.create({
              message: this.translateService.getItemTranslated('CHAT.OWNER_NOTIFIED_TOAST')
                || this.translate.instant('CHAT.OWNER_NOTIFIED_MESSAGE'),
              duration: 4000,
              color: 'success',
            }).then(t => t.present());
            this.showNotificationDialog = false;
            return;
          }
          const notifId: number | undefined =
            this.selectedNotification?.id ||
            this.selectedNotification?.originalData?.id;
          const viewed: boolean =
            this.selectedNotification?.viewed ||
            this.selectedNotification?.originalData?.viewed;
          if (notifId) {
            this.notificationActions.deleteOriginatingNotification(notifId, viewed, this.notifications);
          }

          const recoveryDialogOpened = await this.openRecoveryDialogIfNeeded(result.conversationId);
          if (recoveryDialogOpened) {
            return;
          }

          this.showNotificationDialog = false;
          this.router.navigate(['/app/messages/' + result.conversationId]);
        } else {
          this.toastController.create({
            message: this.translate.instant('NOTIFICATIONS.START_CONVERSATION_ERROR'),
            duration: 3000,
            color: 'danger',
          }).then(t => t.present());
        }
      },
      error: async () => {
        loading.dismiss();
        const t = await this.toastController.create({
          message: this.translate.instant('NOTIFICATIONS.START_CONTACT_ERROR'),
          duration: 3000,
          color: 'danger',
        });
        t.present();
      }
    });
  }

  async onRecoveryDialogMessageSent(content: string): Promise<void> {
    const trimmed = content?.trim();
    if (!trimmed || !this.recoveryDialogConversationId || this.recoveryDialogOwnerFreeMsgConsumed) {
      return;
    }

    try {
      const storedContent = await firstValueFrom(
        this.notificationActions.sendRecoveryMessage(this.recoveryDialogConversationId, trimmed),
      );
      this.recoveryDialogOwnerFreeMsgConsumed = true;
      this.recoveryDialogOwnerFreeMessage = storedContent || trimmed;
    } catch (error: any) {
      const toast = await this.toastController.create({
        message: error?.error?.message || this.translate.instant('CHAT.ERROR_SENDING_MESSAGE'),
        duration: 3000,
        color: 'danger',
      });
      await toast.present();
    }
  }

  onRecoveryDialogUnlocked(): void {
    const conversationId = this.recoveryDialogConversationId;
    this.resetRecoveryDialogState();

    if (conversationId) {
      this.router.navigate(['/app/messages/' + conversationId]);
    }
  }

  onRecoveryDialogDismissed(): void {
    this.resetRecoveryDialogState();
  }

  onRecoveryDialogPublicProfileSettingsRequested(): void {
    this.resetRecoveryDialogState();

    setTimeout(() => {
      this.router.navigate(['/edit-public-profile']);
    }, 200);
  }

  private async openRecoveryDialogIfNeeded(notificationOrConversationId: any): Promise<boolean> {
    const recoveryDialogState = await firstValueFrom(
      this.notificationActions.loadRecoveryDialogState(notificationOrConversationId),
    );

    if (!recoveryDialogState?.shouldOpenDialog) {
      return false;
    }

    this.closeNotificationDialog();
    this.applyRecoveryDialogState(recoveryDialogState);
    return true;
  }

  private applyRecoveryDialogState(recoveryDialogState: RecoveryDialogState): void {
    this.recoveryDialogConversationId = recoveryDialogState.conversationId;
    this.recoveryDialogPremiumChatAmount = recoveryDialogState.premiumChatAmount;
    this.recoveryDialogOwnerFreeMessage = recoveryDialogState.ownerFreeMessage;
    this.recoveryDialogOwnerFreeMsgConsumed = recoveryDialogState.ownerFreeMsgConsumed;
    this.recoveryDialogFinderFreeMessage = recoveryDialogState.finderFreeMessage;
    this.showRecoveryOptionsDialog = true;
  }

  private resetRecoveryDialogState(): void {
    this.showRecoveryOptionsDialog = false;
    this.recoveryDialogConversationId = 0;
    this.recoveryDialogPremiumChatAmount = 0;
    this.recoveryDialogOwnerFreeMessage = '';
    this.recoveryDialogOwnerFreeMsgConsumed = false;
    this.recoveryDialogFinderFreeMessage = '';
  }
}
